import React, { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import './ActiveRoomsDropdown.css';

// Dropdown listing active rooms for a given game kind ('freeplay' or 'cash').
// onJoin(room) is called with the chosen room when the player clicks "Join".
const ActiveRoomsDropdown = ({ kind, onJoin, gameMode = null }) => {
  const { getActiveRooms } = useWebSocket();
  const [open, setOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getActiveRooms();
      const list = kind === 'freeplay' ? (res.freeplay || []) : (res.cash || []);
      // when a game is chosen (Deal / Pool / Points Rummy) only show that game's rooms
      setRooms(gameMode ? list.filter(r => r.gameMode === gameMode) : list);
    } catch (e) {
      setError(e.message || 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, [getActiveRooms, kind, gameMode]);

  useEffect(() => {
    if (!open) return;
    refresh();
    const interval = setInterval(refresh, 10000);
    return () => clearInterval(interval);
  }, [open, refresh]);

  const isCash = kind === 'cash';
  const label = isCash ? 'Cash Rooms' : 'Free Play Rooms';

  return (
    <div className="active-rooms-dropdown">
      <button type="button" className="active-rooms-toggle" onClick={() => setOpen(o => !o)}>
        🎲 {label} ({rooms.length}) <span className="ar-caret">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="active-rooms-panel">
          {loading && <div className="ar-status">Loading rooms…</div>}
          {error && <div className="ar-status ar-error">{error}</div>}
          {!loading && !error && rooms.length === 0 && (
            <div className="ar-status">No active rooms right now.</div>
          )}

          {!loading && rooms.map(room => (
            <div key={room.gameId} className={`ar-room${room.joinable ? '' : ' ar-room-closed'}`}>
              <div className="ar-room-main">
                <span className="ar-room-code">{room.roomCode}</span>
                <span className="ar-room-detail">
                  {isCash && room.betAmount > 0 ? `${room.betAmount} ${room.currency}${room.gameMode === 'rate' ? ' / pt' : ''} · ` : ''}
                  {room.gameMode === 'deal' ? `${room.deals} deals` : (room.gameMode === 'rate' ? 'Points Rummy' : `${room.points} pts`)}
                </span>
              </div>
              <div className="ar-room-meta">
                <span className="ar-room-players">👥 {room.joinedPlayers}/{room.maxPlayers}</span>
                {room.timeRemaining != null && (
                  <span className="ar-room-timer">⏱ {room.timeRemaining}s</span>
                )}
                <button
                  type="button"
                  className="ar-join-btn"
                  disabled={!room.joinable}
                  onClick={() => onJoin(room)}
                >
                  {room.joinable ? 'Join' : 'Closing'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveRoomsDropdown;
